// @ts-nocheck
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimeClock } from '@mui/x-date-pickers/TimeClock';
import { Box, Typography, Button, ToggleButtonGroup, ToggleButton } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

interface TimePickerEditorProps {
  value: string;
  onValueChange: (value: string) => void;
  stopEditing: () => void;
}

const TimePickerEditor = forwardRef((props: TimePickerEditorProps, ref) => {
  const { value, stopEditing } = props;

  // Parse existing value (HH:mm format) or default to 09:00
  const parseInitial = (): Dayjs => {
    if (value && /^\d{2}:\d{2}$/.test(value)) {
      const h = parseInt(value.split(':')[0]);
      const m = parseInt(value.split(':')[1]);
      // Snap to nearest 5-minute interval
      const snapped = Math.round(m / 5) * 5;
      return dayjs().hour(h).minute(snapped >= 60 ? 55 : snapped);
    }
    return dayjs().hour(9).minute(0);
  };

  const [time, setTime] = useState<Dayjs>(parseInitial);
  const [view, setView] = useState<'hours' | 'minutes'>('hours');
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    getValue() {
      return time.format('HH:mm');
    },
    isCancelAfterEnd() {
      return false;
    },
    isPopup() {
      return true;
    },
  }));

  // Focus the container when mounted
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const handleTimeChange = (newTime: Dayjs | null) => {
    if (newTime) {
      setTime(newTime);
      // Auto-switch to minutes after selecting hours
      if (view === 'hours') {
        setTimeout(() => setView('minutes'), 200);
      }
    }
  };

  const handleOk = () => {
    props.onValueChange(time.format('HH:mm'));
    stopEditing();
  };

  const handleCancel = () => {
    stopEditing();
  };

  const handleClear = () => {
    props.onValueChange('');
    stopEditing();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        ref={containerRef}
        tabIndex={0}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 6,
          p: 1.5,
          width: 280,
          outline: 'none',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            handleCancel();
          } else if (e.key === 'Enter') {
            handleOk();
          }
        }}
      >
        {/* Header showing selected time */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: 2 }}>
            <Box
              component="span"
              onClick={() => setView('hours')}
              sx={{
                cursor: 'pointer',
                color: view === 'hours' ? 'primary.main' : 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {time.format('HH')}
            </Box>
            <Box component="span" sx={{ color: 'text.secondary', mx: 0.5 }}>:</Box>
            <Box
              component="span"
              onClick={() => setView('minutes')}
              sx={{
                cursor: 'pointer',
                color: view === 'minutes' ? 'primary.main' : 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {time.format('mm')}
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            24-hour format (IST)
          </Typography>
        </Box>

        {/* Toggle between hours and minutes */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, v) => v && setView(v)}
            size="small"
          >
            <ToggleButton value="hours" sx={{ px: 2, py: 0.5, fontSize: 11 }}>Hours</ToggleButton>
            <ToggleButton value="minutes" sx={{ px: 2, py: 0.5, fontSize: 11 }}>Minutes</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Clock face */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TimeClock
            value={time}
            onChange={handleTimeChange}
            view={view}
            ampm={false}
            minutesStep={5}
            sx={{
              '& .MuiClock-root': { margin: 0 },
              '& .MuiTimeClock-root': { width: 240 },
            }}
          />
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Button size="small" onClick={handleClear} color="error">
            Clear
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleOk}>
              OK
            </Button>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
});

TimePickerEditor.displayName = 'TimePickerEditor';

export default TimePickerEditor;
